/** Catálogo fijo de líneas DDP import (grupo 13). Valor/BaseMultiplicador vienen de BD. */
export const CATALOGO_DDP_IMPORT = [
  {
    subgrupoId: 203,
    codigo: "LCL",
    concepto: "LCL (flete)",
    tipoCalculo: "per_cbm",
    valorDefault: 0,
    orden: 1,
  },
  {
    subgrupoId: 204,
    codigo: "HANDLING",
    concepto: "Handling",
    tipoCalculo: "per_cbm",
    valorDefault: 0,
    orden: 2,
  },
  {
    subgrupoId: 198,
    codigo: "DELIVERY",
    concepto: "Delivery",
    tipoCalculo: "per_cbm",
    valorDefault: 0,
    orden: 3,
  },
  {
    subgrupoId: 205,
    codigo: "DOCS",
    concepto: "Docs",
    tipoCalculo: "fijo_envio",
    valorDefault: 0,
    orden: 4,
  },
  {
    subgrupoId: 206,
    codigo: "IES",
    concepto: "IES",
    tipoCalculo: "fijo_envio",
    valorDefault: 0,
    orden: 5,
  },
  {
    subgrupoId: 193,
    codigo: "CUSTOMS",
    concepto: "Customs",
    tipoCalculo: "fijo_envio",
    valorDefault: 0,
    orden: 6,
  },
  {
    subgrupoId: 208,
    codigo: "T3",
    concepto: "T3",
    tipoCalculo: "fijo_envio",
    valorDefault: 0,
    orden: 7,
  },
  {
    subgrupoId: 210,
    codigo: "INSURANCE",
    concepto: "Insurance",
    tipoCalculo: "pct_seguro",
    valorDefault: 0.2,
    baseMultiplicadorDefault: 1.1,
    orden: 8,
  },
  {
    subgrupoId: 209,
    codigo: "CAUTION",
    concepto: "Caution",
    tipoCalculo: "pct_arancel",
    valorDefault: 0.5,
    orden: 9,
  },
];

export const PER_M3_SUBGRUPO_IDS = new Set([203, 204, 198]);
export const PER_SHIPMENT_SUBGRUPO_IDS = new Set([205, 206, 193, 208]);
export const DDP_AUTO_SUBGRUPO_IDS = new Set(
  CATALOGO_DDP_IMPORT.map((c) => c.subgrupoId),
);

export function parseTarifaNum(val) {
  return parseFloat(String(val ?? "").replace(",", ".")) || 0;
}

export function safeTarifaDecimal(val, decimals = 4) {
  return Number(parseTarifaNum(val).toFixed(decimals));
}

export function getTarifaBySubgrupo(tarifas = [], subgrupoId) {
  return tarifas.find((t) => Number(t.subgrupoId) === Number(subgrupoId));
}

export function getTarifaValor(tarifas = [], subgrupoId) {
  const tarifa = getTarifaBySubgrupo(tarifas, subgrupoId);
  if (!tarifa || tarifa.activo === false) return 0;
  return parseTarifaNum(tarifa.valor);
}

export function getTarifaBaseMultiplicador(tarifas = [], subgrupoId, fallback = 1.1) {
  const tarifa = getTarifaBySubgrupo(tarifas, subgrupoId);
  if (!tarifa || tarifa.activo === false) return fallback;
  const m = parseTarifaNum(tarifa.baseMultiplicador);
  return m > 0 ? m : fallback;
}

export function computeInsuranceFromTarifa(subtotalProveedor, tarifas = []) {
  const pct = getTarifaValor(tarifas, 210) || 0.2;
  const mult = getTarifaBaseMultiplicador(tarifas, 210, 1.1);
  const base = subtotalProveedor * mult;
  const conv = safeTarifaDecimal(base * (pct / 100), 4);
  return { base: safeTarifaDecimal(base, 2), conv, un: safeTarifaDecimal(pct, 6) };
}

export function computeCautionFromTarifa(arancelAmount, tarifas = []) {
  const pct = getTarifaValor(tarifas, 209) || 0.5;
  const conv = safeTarifaDecimal(parseTarifaNum(arancelAmount) * (pct / 100), 4);
  return { conv, un: safeTarifaDecimal(pct, 6) };
}

export function computePerCbmLine(volPerUnit, tarifas = [], subgrupoId) {
  const coste = getTarifaValor(tarifas, subgrupoId);
  const vol = parseTarifaNum(volPerUnit);
  return {
    un: safeTarifaDecimal(vol, 6),
    coste: safeTarifaDecimal(coste, 2),
    costeConvertido: safeTarifaDecimal(vol * coste, 4),
  };
}

export function computeFijoEnvioLine(tarifas = [], subgrupoId) {
  const coste = getTarifaValor(tarifas, subgrupoId);
  return {
    un: 1,
    coste: safeTarifaDecimal(coste, 2),
    costeConvertido: safeTarifaDecimal(coste, 4),
  };
}

/** Construye payload de líneas DDP import para persistir en presupuesto. */
export function buildDdpImportLines({
  volPerUnit,
  subtotalProveedor,
  dutyPct,
  tarifas = [],
}) {
  const insurance = computeInsuranceFromTarifa(subtotalProveedor, tarifas);
  const lcl = computePerCbmLine(volPerUnit, tarifas, 203);
  const subtotalBaseImport =
    subtotalProveedor + insurance.conv + lcl.costeConvertido;
  const duty = parseTarifaNum(dutyPct);
  const arancelConvCost = safeTarifaDecimal(subtotalBaseImport * (duty / 100), 4);
  const caution = computeCautionFromTarifa(arancelConvCost, tarifas);

  const autoLinesBase = [
    {
      ID_SUBGRUPO: 210,
      Un: insurance.un,
      Coste: insurance.base,
      CosteConvertido: insurance.conv,
    },
    {
      ID_SUBGRUPO: 203,
      Un: lcl.un,
      Coste: lcl.coste,
      CosteConvertido: lcl.costeConvertido,
    },
    {
      ID_SUBGRUPO: 192,
      Un: safeTarifaDecimal(duty, 6),
      Coste: safeTarifaDecimal(subtotalBaseImport, 2),
      CosteConvertido: arancelConvCost,
    },
    {
      ID_SUBGRUPO: 209,
      Un: caution.un,
      Coste: safeTarifaDecimal(arancelConvCost, 2),
      CosteConvertido: caution.conv,
    },
  ];

  const adicionales = [204, 198, 205, 206, 193, 208].map((id) => {
    const line = PER_M3_SUBGRUPO_IDS.has(id)
      ? computePerCbmLine(volPerUnit, tarifas, id)
      : computeFijoEnvioLine(tarifas, id);
    return {
      ID_SUBGRUPO: id,
      Un: line.un,
      Coste: line.coste,
      CosteConvertido: line.costeConvertido,
    };
  });

  return [...autoLinesBase, ...adicionales];
}

export function tipoCalculoLabel(tipoCalculo) {
  switch (tipoCalculo) {
    case "per_cbm":
      return "€/m³ × VOL";
    case "fijo_envio":
      return "€ fijo × 1 envío";
    case "pct_seguro":
      return "% sobre subtotal × mult.";
    case "pct_arancel":
      return "% sobre arancel";
    default:
      return tipoCalculo;
  }
}
